import React from 'react'
import { t } from '../../i18n'
import Icon from '../shared/icon'
import { HUMAN_SLOTS } from '../stock_control/types'
import { LocalizationContextType } from '../shared/contexts'
import { formatTenantDate } from '../../utils/formatTenantDateTime'
import { TIME_SOURCE } from '../../utils/timeSource'

const scope = 'replenishment.sidebar.history'

interface Product {
  id: number
  name: string
  imageUrl: string
  ancestry_tree?: string
  feeder_subtype?: 'fridge' | 'ambient'
  max_container_amount?: number
}

interface ProductReplenishment {
  id: number
  containerType: 'solid' | 'liquid'
  slotNumber: number
  state: string
  replenishmentType: 'manual' | 'auto'
  product: Product
  previousProduct: Product | null
}

interface PlateReplenishment {
  id: number
  slotNumber: number
  amount: number
  state: string
}

interface OilReplenishment {
  id: number
  amount: number
  state: string
}

interface ReplenishmentOrderData {
  id: number
  state: string
  deliveryDate: string
  editableOrderId: number | null
  createdAt: string
  productReplenishments: ProductReplenishment[]
  plateReplenishments: PlateReplenishment[]
  oilReplenishment: OilReplenishment | null
}

interface ReplenishmentOrderProps {
  replenishmentOrder: ReplenishmentOrderData
  localization_context?: LocalizationContextType
}

const ProductItem = ({ item }: { item: ProductReplenishment }) => {
  const isRefill = item.product?.id === item.previousProduct?.id
  const showPreviousProduct = !isRefill && item.previousProduct
  const actionName = isRefill
    ? t('refill', { scope: 'replenishment.sidebar' })
    : t('product_change', { scope: 'replenishment.sidebar' })

  const renderFeederSubtypeIcon = (feederSubtype?: string) => {
    return feederSubtype === 'fridge' ? (
      <Icon name='fridge' color='#2D5CF2' />
    ) : (
      <Icon name='container' />
    )
  }

  return (
    <div className='replenishment-item'>
      <div className={`item-image ${showPreviousProduct ? 'with-change' : ''}`}>
        {showPreviousProduct && (
          <img
            className='old-image'
            src={item.previousProduct?.imageUrl || '/images/product-placeholder.svg'}
            alt='Previous product'
          />
        )}
        <img
          className={showPreviousProduct ? 'new-image' : ''}
          src={item.product?.imageUrl || '/images/missing_image.svg'}
          alt={item.product?.name}
        />
        <span className='slot-badge'>#{item.slotNumber}</span>
      </div>
      <div className='item-info'>
        {showPreviousProduct && (
          <div className='previous-name'>
            {item.previousProduct?.name || t('empty_container', { scope: 'replenishment.sidebar' })}
          </div>
        )}
        <div className='current-name'>{item.product?.name}</div>
        {item.product?.max_container_amount && (
          <div className='product-details'>{item.product.max_container_amount}g</div>
        )}
        <div className='storage-location'>
          {renderFeederSubtypeIcon(item.product?.feeder_subtype)}
          <span>
            {t(`feeder_subtypes.${item.product?.feeder_subtype || 'ambient'}`, { scope: 'product' })} | {actionName}
          </span>
        </div>
      </div>
      <div className='item-badges'>
        <div
          className={`replenishment-type-badge replenishment-type-badge-${item.replenishmentType}`}
        >
          {t(`replenishment_types.${item.replenishmentType}`, { scope: 'replenishment.sidebar' })}
        </div>
        <div className={`item-state state-${item.state}`}>
          {item.state.replace(/_/g, ' ')}
        </div>
      </div>
    </div>
  )
}

const PlateItem = ({ item }: { item: PlateReplenishment }) => {
  return (
    <div className='replenishment-item'>
      <div className='item-image'>
        <div className='icon-container'>
          <Icon name='plate' color='white' />
        </div>
        <span className='slot-badge'>#{HUMAN_SLOTS[item.slotNumber - 1]}</span>
      </div>
      <div className='item-info'>
        <div className='current-name'>
          {t('replenishment.bowls.bowl_stand', { slotNumber: HUMAN_SLOTS[item.slotNumber - 1] })}
        </div>
        <div className='item-action'>{item.amount} pcs refill</div>
      </div>
      <div className={`item-state state-${item.state}`}>
        {item.state.replace(/_/g, ' ')}
      </div>
    </div>
  )
}

const OilItem = ({ item }: { item: OilReplenishment }) => {
  return (
    <div className='replenishment-item'>
      <div className='item-image'>
        <div className='icon-container'>
          <Icon name='oil_tank' color='white' />
        </div>
      </div>
      <div className='item-info'>
        <div className='current-name'>Oil tank</div>
        <div className='item-action'>{item.amount}L refill</div>
      </div>
      <div className={`item-state state-${item.state}`}>
        {item.state.replace(/_/g, ' ')}
      </div>
    </div>
  )
}

const ReplenishmentOrder = ({ replenishmentOrder, localization_context }: ReplenishmentOrderProps) => {
  const solidProducts = replenishmentOrder.productReplenishments.filter(
    (r: ProductReplenishment) => r.containerType === 'solid'
  )

  const liquidProducts = replenishmentOrder.productReplenishments.filter(
    (r: ProductReplenishment) => r.containerType === 'liquid'
  )

  const formattedDate = formatTenantDate(
    replenishmentOrder.deliveryDate || replenishmentOrder.createdAt,
    localization_context
  )

  return (
    <div className='replenishment-order-details-page'>
      <div className='page-header'>
        <div className='header-left'>
          <h1 className='order-title'>
            {t('replenishment_order_name', { scope, orderId: replenishmentOrder.id })}
          </h1>
        </div>
        <div className='header-right'>
          <span className={`order-state state-${replenishmentOrder.state}`}>
            {replenishmentOrder.state}
          </span>
          <span className='order-date' title={TIME_SOURCE.app}>{formattedDate}</span>
        </div>
      </div>

      <div className='order-content'>
        {solidProducts.length > 0 && (
          <div className='replenishment-section'>
            <div className='section-header'>Solid Ingredients</div>
            <div className='section-items'>
              {solidProducts.map((item: ProductReplenishment) => (
                <ProductItem key={item.id} item={item} />
              ))}
            </div>
          </div>
        )}

        {liquidProducts.length > 0 && (
          <div className='replenishment-section'>
            <div className='section-header'>Liquid Ingredients</div>
            <div className='section-items'>
              {liquidProducts.map((item: ProductReplenishment) => (
                <ProductItem key={item.id} item={item} />
              ))}
            </div>
          </div>
        )}

        {replenishmentOrder.plateReplenishments.length > 0 && (
          <div className='replenishment-section'>
            <div className='section-header'>{t('bowls', { scope })}</div>
            <div className='section-items'>
              {replenishmentOrder.plateReplenishments.map((item: PlateReplenishment) => (
                <PlateItem key={item.id} item={item} />
              ))}
            </div>
          </div>
        )}

        {replenishmentOrder.oilReplenishment && (
          <div className='replenishment-section'>
            <div className='section-header'>{t('oil', { scope })}</div>
            <div className='section-items'>
              <OilItem item={replenishmentOrder.oilReplenishment} />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default ReplenishmentOrder

